import { useState, useEffect, useMemo } from "react";

export default function DatePicker({ value, onChange, allowPresent = false }) {
    const parseDate = (str) => {
        if (!str) return { day: "", month: "", year: "" };
        const [year, month, day] = str.split("-");
        // Remove leading zeros to match dropdown option values
        return {
            day: day ? String(parseInt(day, 10)) : "",
            month: month ? String(parseInt(month, 10)) : "",
            year
        };
    };

    const { day: initialDay, month: initialMonth, year: initialYear } = parseDate(value);
    const [day, setDay] = useState(initialDay);
    const [month, setMonth] = useState(initialMonth);
    const [year, setYear] = useState(initialYear);
    const [present, setPresent] = useState(value === "Present");

    // Update internal state when value prop changes
    useEffect(() => {
        const parsed = parseDate(value);
        setDay(parsed.day);
        setMonth(parsed.month);
        setYear(parsed.year);
        setPresent(value === "Present");
    }, [value]);

    useEffect(() => {
        if (present) {
            onChange("Present");
        } else if (day && month && year) {
            const formatted = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            onChange(formatted);
        }
    }, [day, month, year, present]);

    const currentYear = new Date().getFullYear();
    const years = useMemo(() => {
        return Array.from({ length: currentYear - 1900 + 1 }, (_, i) => String(currentYear - i));
    }, [currentYear]);

    const daysInMonth = (y, m) => new Date(y, m, 0).getDate();
    const days = useMemo(() => {
        if (!month || !year) return Array.from({ length: 31 }, (_, i) => String(i + 1));
        return Array.from({ length: daysInMonth(year, month) }, (_, i) => String(i + 1));
    }, [month, year]);

    const handleChange = (setter) => (e) => {
        setter(e.target.value);
        setPresent(false);
    };

    return (
        <div className="flex gap-2 items-center">
            {!present && (
                <>
                    <select className="border rounded p-2" value={day} onChange={handleChange(setDay)}>
                        <option value="">DD</option>
                        {days.map((d) => (
                            <option key={d} value={d}>
                                {d.padStart(2, "0")}
                            </option>
                        ))}
                    </select>

                    <select className="border rounded p-2" value={month} onChange={handleChange(setMonth)}>
                        <option value="">MM</option>
                        {Array.from({ length: 12 }, (_, i) => String(i + 1)).map((m) => (
                            <option key={m} value={m}>
                                {m.padStart(2, "0")}
                            </option>
                        ))}
                    </select>

                    <select className="border rounded p-2" value={year} onChange={handleChange(setYear)}>
                        <option value="">YYYY</option>
                        {years.map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </select>
                </>
            )}

            {allowPresent && (
                <label className="flex items-center space-x-2 text-sm">
                    <input
                        type="checkbox"
                        checked={present}
                        onChange={(e) => setPresent(e.target.checked)}
                    />
                    <span>Present</span>
                </label>
            )}
        </div>
    );
}
